import React, { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadingSpinner from "../../common/LoadingSpinner";
import Icon from "../../ui/Icon";

const OperatorAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // React StrictMode에서 useEffect가 두 번 실행되는 것을 방지
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 이미 처리되었으면 스킵 (StrictMode 대응)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // URL에서 token, error, message 파라미터 추출
    const token = searchParams.get("token");
    const error = searchParams.get("error");
    const errorMessage = searchParams.get("message");

    // 에러 처리 (error=true 또는 error 파라미터 존재)
    if (error === "true" || error) {
      // 에러 메시지 디코딩 및 표시
      const displayMessage = errorMessage
        ? decodeURIComponent(errorMessage)
        : "로그인에 실패했습니다.";

      toast.error(displayMessage);

      // 3초 후 로그인 페이지로 리다이렉트
      setTimeout(() => {
        navigate("/operator/auth");
      }, 3000);
      return;
    }

    // 성공 처리 (token 존재)
    if (token) {
      // 토큰을 localStorage에 저장
      localStorage.setItem("operatorAccessToken", token);

      toast.success("로그인에 성공했습니다!");

      // 약간의 딜레이 후 대시보드로 리다이렉트 (사용자가 성공 메시지를 볼 수 있도록)
      setTimeout(() => {
        navigate("/operator/dashboard", { replace: true });
      }, 500);
      return;
    }

    // 토큰도 에러도 없는 경우 (알 수 없는 상태)
    toast.error("인증 정보를 받지 못했습니다.");

    setTimeout(() => {
      navigate("/operator/auth");
    }, 2000);
  }, [searchParams, navigate]);

  // 에러 상태 확인
  const error = searchParams.get("error");
  const errorMessage = searchParams.get("message");

  // 에러가 있으면 에러 UI 표시
  if (error) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="alertTriangle" className="text-red-600" size="xl" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            로그인 실패
          </h2>
          <p className="text-gray-600 text-sm mb-4 whitespace-pre-wrap">
            {errorMessage
              ? decodeURIComponent(errorMessage)
              : "알 수 없는 오류가 발생했습니다."}
          </p>
          <p className="text-gray-500 text-xs">
            3초 후 로그인 페이지로 이동합니다...
          </p>
        </div>
      </div>
    );
  }

  // 로딩 중 (에러가 없으면 항상 로딩 표시)
  return (
    <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
        <LoadingSpinner size="lg" className="mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          로그인 처리 중...
        </h2>
        <p className="text-gray-600 text-sm">잠시만 기다려주세요.</p>
      </div>
    </div>
  );
};

export default OperatorAuthCallback;
